import { Search } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';
import { InputAdornment, InputLabel, MenuItem, Select } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { makeStyles } from 'tss-react/mui';
import { PaymentPlanResponse, PermissionsGroupId, TargetPlatform } from '../../../../API';
import { useData } from '../../../../data-layer';
import { useLocales, useNotifications } from '../../../../hooks';
import { withPlansSplitPane } from '../../../../state/General';
import { withPlansFilters } from '../../../../state/Payments/withPaymentsFilters';
import { ALL } from '../../../../utils/types/genericTypes';
import Button from '../../../shared/Button';
import CountryBadge from '../../../shared/CountryBadge';
import CountryPickerLite from '../../../shared/CountryPickerLite';
import { DataGrid, DataGridColDef } from '../../../shared/DataGrid';
import FormControl from '../../../shared/FormControl';
import { IdBadge } from '../../../shared/IdBadge';
import TextField from '../../../shared/TextField';
import CouponBadge from '../../Coupons/CouponBadge/CouponBadge';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { AppRoutes } from '../../../../Routes';
import { useNavigate } from 'react-router-dom';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { plansGridTestIds } from '../../../shared/TestsIds';
import Pagination from '../../../shared/Pagination';
import { PaginationMeta } from '../../../../API';
import { DEFAULT_PAGINATION_LIMIT } from '../../../../utils/appDefaults';
import { PlanSearchFilters } from '../../../../data-layer/Controllers/Payments/Plans/PlansHook';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: theme.spacing(4)
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4)
  },
  ingestButton: {
    marginLeft: 'auto'
  },
  textFilter: {
    width: 400,
    marginBottom: '0 !important'
  },
  planGroupFilter: {
    width: 250
  },
  countryFilter: {
    width: 350
  },
  gridContainer: {
    display: 'flex',
    flexGrow: 1
  },
  paginationContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingTop: theme.spacing(3)
  },
  planPriceContainer: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  couponBadgeContainer: {
    display: 'inline-block',
    margin: theme.spacing(0, 2, 2, 0)
  },
  couponBadgesContainer: {
    marginBottom: `calc(0px - ${theme.spacing(2)})`
  }
}));

export function PlansGrid(): JSX.Element {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { cx, classes } = useStyles();
  const {
    plans: {
      state: { withPlanGroups, withFormMetadata },
      hook: { edit, searchPlans, ingestPlans }
    }
  } = useData();
  const planGroups = useRecoilValue(withPlanGroups);

  const { notifySuccess } = useNotifications();
  const { hasReadPermission, hasUpsertPermission } = usePermissions();
  const { canRead, canSave } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const hasMonetizationReadPermissions = hasReadPermission(PermissionsGroupId.MONETIZATION) || canRead;
  const canIngestPlans = hasUpsertPermission(PermissionsGroupId.MONETIZATION) || canSave;

  const { isShowingForm } = useRecoilValue(withFormMetadata);
  const [filters, setFilters] = useRecoilState(withPlansFilters);
  const [plans, setPlans] = useState<PaymentPlanResponse[] | undefined>();
  const [meta, setMeta] = useState<PaginationMeta | undefined>();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGINATION_LIMIT);
  const [isIngesting, setIsIngesting] = useState(false);

  // A filter change always returns the user to the first page, otherwise they
  // could land on a page that no longer exists in the narrowed result set.
  useEffect(() => {
    setPage(1);
  }, [filters, limit]);

  useEffect(() => {
    loadPlans();
  }, [filters, page, limit]);

  // The visible page is local state rather than a shared cache, so a plan edited
  // in the form would not otherwise reflect the change until the next filter or
  // page change. Reload once the form closes.
  const wasShowingForm = useRef(isShowingForm);
  useEffect(() => {
    if (wasShowingForm.current && !isShowingForm) {
      loadPlans();
    }
    wasShowingForm.current = isShowingForm;
  }, [isShowingForm]);

  useEffect(() => {
    if (!hasMonetizationReadPermissions) {
      navigate(AppRoutes.welcome);
    }
  }, [hasMonetizationReadPermissions]);

  const columns: DataGridColDef<PaymentPlanResponse>[] = [
    {
      key: 'name',
      cellRenderer: (plan) => plan.name,
      label: t('payments.name'),
      sortFunction: (a, b) => a.name.localeCompare(b.name),
      baseWidth: '14%'
    },
    {
      key: 'sku',
      cellRenderer: (plan) => <IdBadge id={plan.code} />,
      label: 'SKU',
      sortFunction: (a, b) => a.code.localeCompare(b.code),
      baseWidth: '14%'
    },
    {
      key: 'promoGroup',
      cellRenderer: (plan) => plan.promoGroup,
      label: t('payments.promo_group'),
      sortFunction: (a, b) => {
        if (!a.promoGroup) return -1;
        if (!b.promoGroup) return 1;
        return a.promoGroup.localeCompare(b.promoGroup);
      },
      baseWidth: '10%'
    },
    {
      key: 'localizationTemplateId',
      cellRenderer: (plan) => plan.localizationTemplateId,
      label: t('payments.template_id'),
      sortFunction: (a, b) => a.localizationTemplateId.localeCompare(b.localizationTemplateId),
      baseWidth: '10%'
    },
    {
      key: 'price',
      cellRenderer: (plan) => (
        <div className={classes.planPriceContainer}>
          <div>{plan.price}</div>
          <div>{plan.currency}</div>
        </div>
      ),
      label: t('payments.base_price'),
      baseWidth: '8%'
    },
    {
      key: 'billing',
      cellRenderer: (plan) => plan.billingPeriodLength + ' ' + plan.billingPeriodUnit,
      label: t('payments.billing_period'),
      baseWidth: '8%'
    },
    {
      key: 'device',
      cellRenderer: (plan) => t(`platforms.platform_name.${plan.device}`),
      label: t('payments.device'),
      sortFunction: (a, b) => a.device.localeCompare(b.device),
      baseWidth: '8%'
    },
    {
      key: 'autoRenew',
      cellRenderer: (plan) => (plan.autoRenew ? 'Yes' : 'No'),
      label: t('payments.auto_renew'),
      sortFunction: (a, b) => String(a.autoRenew).localeCompare(String(b.autoRenew)),
      baseWidth: '8%'
    },
    {
      key: 'country',
      cellRenderer: (plan) => <CountryBadge countryCode={plan.country.toUpperCase()} />,
      label: t('general.country'),
      sortFunction: (a, b) => a.country.localeCompare(b.country),
      baseWidth: '8%'
    },
    {
      key: 'coupons',
      cellRenderer: (plan) => (
        <div className={cx({ [classes.couponBadgesContainer]: !!plan.coupons?.length })}>
          {plan.coupons?.map((coupon, i) => (
            <div key={i} className={classes.couponBadgeContainer}>
              <CouponBadge couponCode={coupon} />
            </div>
          ))}
        </div>
      ),
      label: t('payments.coupons'),
      baseWidth: '20%'
    }
  ];

  const onPlanClick = (plan: PaymentPlanResponse) => {
    edit(plan.code);
  };

  const onIngestClick = async () => {
    setIsIngesting(true);
    const ingested = await ingestPlans();
    if (ingested) {
      // ingestPlans refreshes the cache behind PlanBadge, but the grid renders a
      // page fetched through searchPlans, so it has to be reloaded separately.
      await loadPlans();
      notifySuccess(t('payments.ingest_plans_success'));
    }
    setIsIngesting(false);
  };

  const onTextFilterChanged: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
    setFilters({
      ...filters,
      text: evt.target.value
    });
  };

  const onPromoGroupFilterChanged = (evt: SelectChangeEvent<string>) => {
    setFilters({
      ...filters,
      promoGroup: evt.target.value
    });
  };

  const onDeviceFilterChanged = (evt: SelectChangeEvent<string>) => {
    setFilters({
      ...filters,
      device: evt.target.value
    });
  };

  const onCountryFilterChanged = (countries: string[]) => {
    setFilters({
      ...filters,
      countries
    });
  };

  /**
   * `ALL` and the empty string are UI-only sentinels meaning "no filter"; the
   * API expects the parameter to be absent instead.
   */
  const toFilterValue = (value?: string) => (!value || value === ALL ? undefined : value);

  /**
   * The API filters by a single country and a plan belongs to exactly one, so
   * the picker allows one selection. Filters persisted before that change can
   * still hold several, and showing more chips than are actually applied would
   * misrepresent the result set - so only the first is ever used.
   */
  const selectedCountries = filters.countries.slice(0, 1);

  const loadPlans = async () => {
    const searchFilters: PlanSearchFilters = {
      text: toFilterValue(filters.text),
      promoGroup: toFilterValue(filters.promoGroup),
      device: toFilterValue(filters.device),
      country: toFilterValue(selectedCountries[0])
    };

    setPlans(undefined);
    const body = await searchPlans({ page, limit, filters: searchFilters });
    setPlans(body.results);
    setMeta(body.meta);
  };

  return (
    <div className={classes.root} data-testid={plansGridTestIds.root}>
      <div className={classes.filters}>
        <TextField
          clearable
          debounced
          label={t('payments.filter_by_keywords')}
          className={classes.textFilter}
          value={filters.text}
          onChange={onTextFilterChanged}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
          data-testid={plansGridTestIds.textFilter}
        />
        <FormControl className={classes.planGroupFilter}>
          <InputLabel shrink variant="standard">
            {t('payments.filter_by_promo_group')}
          </InputLabel>
          <Select
            value={filters.promoGroup}
            onChange={onPromoGroupFilterChanged}
            data-testid={plansGridTestIds.planGroupFilter}
          >
            <MenuItem value={ALL} data-testid={plansGridTestIds.planGroupFilterOption}>
              {t('filters.all')}
            </MenuItem>
            {planGroups?.map((planGroup) => (
              <MenuItem key={planGroup} value={planGroup} data-testid={plansGridTestIds.planGroupFilterOption}>
                {planGroup}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.planGroupFilter}>
          <InputLabel shrink variant="standard">
            {t('payments.filter_by_device')}
          </InputLabel>
          <Select value={filters.device} onChange={onDeviceFilterChanged} data-testid={plansGridTestIds.deviceFilter}>
            <MenuItem value={ALL} data-testid={plansGridTestIds.deviceFilterOption}>
              {t('filters.all')}
            </MenuItem>
            {Object.values(TargetPlatform).map((device) => (
              <MenuItem key={device} value={device} data-testid={plansGridTestIds.deviceFilterOption}>
                {t(`platforms.platform_name.${device}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CountryPickerLite
          singleSelection
          className={classes.countryFilter}
          label={t('payments.filter_by_country')}
          value={selectedCountries}
          onChange={onCountryFilterChanged}
          data-testid={plansGridTestIds.countryFilter}
        />
        <Button
          className={classes.ingestButton}
          loading={isIngesting}
          disabled={!canIngestPlans}
          onClick={onIngestClick}
          data-testid={plansGridTestIds.ingestButton}
        >
          {t('payments.ingest_plans')}
        </Button>
      </div>
      <div className={classes.gridContainer}>
        <DataGrid
          rows={plans}
          columns={columns}
          onRowClick={(plan) => onPlanClick(plan)}
          withColumnSizes={withPlansSplitPane}
          useVirtualization
          rowKey="code"
        />
      </div>
      <div className={classes.paginationContainer}>
        <Pagination queryMetaData={meta} onPageChange={setPage} onResultsChange={setLimit} />
      </div>
    </div>
  );
}
